import type { CareGiverLeadFormData } from "~www/types/forms/care-giver-lead-form";
import type { JobPosting } from "~www/types/job";

import { FormError } from "@homeinstead/ui/components/FormError";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@homeinstead/ui/components/Select";
import { motion } from "motion/react";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";

export function JobSelect({
  locationCareers,
  open,
  onOpenChange,
}: {
  locationCareers: JobPosting[] | undefined;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const { control, watch, setValue } = useFormContext<CareGiverLeadFormData>();

  const officeNumber = watch("step2.organizationNumber");
  const currentJobID = watch("step2.jobID");

  // Auto-select the first job unless a valid job is already selected (e.g. pre-filled from job details page).
  useEffect(() => {
    if (locationCareers && locationCareers.length > 0) {
      const isValidCareerSelected = locationCareers.some(
        (job) => String(job.jobID) === String(currentJobID),
      );
      if (!isValidCareerSelected) {
        setValue("step2.jobID", String(locationCareers[0].jobID), {
          shouldValidate: true,
        });
      }
    }
  }, [locationCareers, setValue, currentJobID]);

  // Find the selected job title for display
  const selectedJob = locationCareers?.find(
    (job) => String(job.jobID) === String(currentJobID),
  );

  return locationCareers && locationCareers.length > 0 ? (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -5 }}
      initial={{ opacity: 0, y: -5 }}
      key="job-select"
      transition={{ duration: 0.2, ease: "easeInOut" }}
    >
      <Controller
        control={control}
        name={"step2.jobID"}
        render={({ field }) => (
          <Select
            defaultValue={String(locationCareers[0].jobID)}
            name="step2.jobID"
            onOpenChange={onOpenChange}
            onValueChange={(value) => {
              field.onChange(value);
            }}
            open={open}
            value={field.value ? String(field.value) : undefined}
          >
            <SelectTrigger label="Job">
              <SelectValue
                placeholder={
                  selectedJob?.jobTitle || locationCareers[0].jobTitle
                }
              />
            </SelectTrigger>
            <SelectContent>
              {locationCareers.map((job) => (
                <SelectItem key={job.jobID} value={String(job.jobID)}>
                  {job.jobTitle}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
    </motion.div>
  ) : (
    officeNumber > 0 && (
      <motion.p
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -5 }}
        initial={{ opacity: 0, y: -5 }}
        key="no-jobs"
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
        <FormError.span>No jobs found.</FormError.span>
      </motion.p>
    )
  );
}
